import SoftBox from "soft_dashboard/components/SoftBox"
import SoftTypography from "soft_dashboard/components/SoftTypography"

import DashboardLayout from "soft_dashboard/examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "soft_dashboard/examples/Navbars/DashboardNavbar"

import React from "react"
import Time from "../../core/Time/Time"
import OrderStatusBadge from "../OrderStatusBadge"
import StaffDataTable from "../../core/StaffDataTable/StaffDataTable"
import Axios from "../../../Util/Axios"
import { useDispatch } from "react-redux"
import { updateToastData } from "../../../slices/toastSlice"
import {
  TOAST_SEVERITY_ERROR,
  TOAST_SEVERITY_SUCCESS,
} from "../../core/Toast/Toast"
import { Checkbox, Button, Box, useMediaQuery, useTheme } from "@mui/material"

const StaffOrderList = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const dispatch = useDispatch()
  const [refreshKey, setRefreshKey] = React.useState(0)
  const [selectedIds, setSelectedIds] = React.useState(new Set())
  const [allRows, setAllRows] = React.useState([])

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedIds.size === allRows.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(allRows.map(r => r.customer_facing_id)))
    }
  }

  const deleteSelected = () => {
    if (!selectedIds.size) return
    if (!window.confirm(`Delete ${selectedIds.size} order(s)?`)) return
    Promise.all(
      [...selectedIds].map(id =>
        Axios.post(`/api/staff-order-detail/${id}/action_delete/`).catch(() => id)
      )
    ).then((results) => {
      const failed = results.filter(r => typeof r === 'string')
      if (failed.length) {
        dispatch(updateToastData({ message: `Failed to delete ${failed.length} order(s)`, severity: TOAST_SEVERITY_ERROR }))
      } else {
        dispatch(updateToastData({ message: `Deleted ${selectedIds.size} order(s)`, severity: TOAST_SEVERITY_SUCCESS }))
      }
      setSelectedIds(new Set())
      setRefreshKey(k => k + 1)
    })
  }
  const allColumns = [
    { name: "select", align: "center" },
    { name: "id", align: "left" },
    { name: "customer_facing_id", align: "left" },
    { name: "created_at", align: "left" },
    { name: "name", align: "left" },
    { name: "type", align: "left" },
    { name: "status", align: "left" },
    { name: "summary", align: "left" },
    { name: "complete_at", align: "left" },
    { name: "user_page", align: "left" },
    { name: "staff_page", align: "left" },
  ]
  const mobileColumns = [
    { name: "name", align: "left" },
    { name: "type", align: "left" },
    { name: "status", align: "left" },
    { name: "staff_page", align: "left" },
  ]
  const columns = isMobile ? mobileColumns : allColumns
  const getRow = (orderData) => {
    return {
      select: (
        <Checkbox
          size="small"
          checked={selectedIds.has(orderData.customer_facing_id)}
          onChange={() => toggleSelect(orderData.customer_facing_id)}
          sx={{ p: 0 }}
        />
      ),
      id: (
        <SoftBox ml={2}>
          <SoftTypography variant="button" fontWeight="medium">
            {orderData.id}
          </SoftTypography>
        </SoftBox>
      ),
      customer_facing_id: (
        <SoftBox ml={2}>
          <SoftTypography variant="button" fontWeight="medium">
            {orderData.customer_facing_id}
          </SoftTypography>
        </SoftBox>
      ),
      name: (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          {orderData.address_full_name}
        </SoftTypography>
      ),
      created_at: (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          <Time>{orderData.created_at}</Time>
        </SoftTypography>
      ),
      type: (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          {orderData.type_display}
        </SoftTypography>
      ),
      status: (
        <SoftBox>
          <OrderStatusBadge
            status={orderData.status}
            statusDisplay={orderData.status_display}
          />
        </SoftBox>
      ),
      summary: (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          {orderData.summary}
        </SoftTypography>
      ),
      complete_at: (
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          {orderData.complete_at && <Time>{orderData.complete_at}</Time>}
        </SoftTypography>
      ),
      user_page: (
        <SoftBox ml={2}>
          <SoftTypography
            component="a"
            href={`/custom-surfboard-order-staff/${orderData?.customer_facing_id}/`}
            variant="caption"
            color="secondary"
            fontWeight="medium"
          >
            View
          </SoftTypography>
        </SoftBox>
      ),
      staff_page: (
        <SoftBox ml={2}>
          <SoftTypography
            component="a"
            href={`/staff/orders/${orderData.customer_facing_id}`}
            variant="caption"
            color="primary"
            fontWeight="medium"
          >
            View
          </SoftTypography>
        </SoftBox>
      ),
    }
  }

  const dataUrl = "/api/staff-order-list/"

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <StaffDataTable
            key={refreshKey}
            title={"Orders"}
            columns={columns}
            getRow={getRow}
            dataUrl={dataUrl}
            onRowsLoaded={(rows) => { setAllRows(rows); setSelectedIds(new Set()) }}
            headerExtra={selectedIds.size > 0 && (
              <Button
                size="small"
                variant="contained"
                onClick={deleteSelected}
                sx={{ fontSize: "0.7rem", textTransform: "none", bgcolor: "#d32f2f", color: "#fff", "&:hover": { bgcolor: "#b71c1c" } }}
              >
                Delete {selectedIds.size} selected
              </Button>
            )}
            actions={[
              {
                name: "Custom Order",
                color: "primary",
                onAction: () => {
                  Axios.post("/api/staff-order-detail/").then(({ data }) => {
                    window.open(`/custom-surfboard-order-staff/${data.customer_facing_id}/`)
                  })
                },
              },
              {
                name: "Glassing Order",
                color: "info",
                onAction: () => {
                  Axios.post("/api/staff-order-detail/", { type: "glassing" }).then(({ data }) => {
                    window.open(`/staff/orders/${data.customer_facing_id}`)
                  })
                },
              },
              {
                name: "Stock Order",
                color: "success",
                onAction: () => {
                  Axios.post("/api/staff-order-detail/", { type: "stock" }).then(({ data }) => {
                    window.open(`/staff/orders/${data.customer_facing_id}`)
                  })
                },
              },
            ]}
            filters={[
              {
                title: "Status",
                title_key: "status",
                options: [
                  { key: "not_draft", value: "Not Draft" },
                ],
              },
              {
                title: "Type",
                title_key: "type",
                options: [
                  { key: "custom", value: "Custom" },
                  { key: "glassing", value: "Glassing" },
                  { key: "stock", value: "Stock" },
                ],
              },
            ]}
          />
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  )
}

export default StaffOrderList
